import React from 'react';
import { X, FileText, User, Calendar, Trash2, Plus, Layers, Eye, ChevronDown, ChevronUp, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AIContext {
  id: string;
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  label: string;
}

interface AIContextManagerProps {
  contexts: AIContext[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
  onAddFullPage: () => void;
}

export const AIContextManager: React.FC<AIContextManagerProps> = ({
  contexts,
  onRemove,
  onClearAll,
  onAddFullPage,
}) => {
  const [expandedContexts, setExpandedContexts] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedContexts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  const getContextIcon = (type: string) => {
    switch (type) {
      case 'patient':
        return <User className="w-4 h-4" />;
      case 'appointment':
        return <Calendar className="w-4 h-4" />;
      case 'record':
        return <FileText className="w-4 h-4" />;
      case 'fullPage':
      case 'page':
        return <Layers className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getContextColor = (type: string) => {
    switch (type) {
      case 'patient':
        return 'from-blue-500 to-blue-600';
      case 'appointment':
        return 'from-green-500 to-green-600';
      case 'record':
        return 'from-purple-500 to-purple-600';
      case 'fullPage':
      case 'page':
        return 'from-orange-500 to-orange-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getContextDetails = (context: AIContext) => {
    switch (context.type) {
      case 'patient': {
        const patientName = typeof context.data.name === 'string'
          ? context.data.name
          : context.data.name?.en || context.data.name?.ar || '';
        const allergies = typeof context.data.allergies === 'string'
          ? context.data.allergies
          : context.data.allergies?.en || context.data.allergies?.ar || '';
        return (
          <div className="text-xs text-gray-600 mt-1">
            <div>Name: {patientName}</div>
            <div>DOB: {context.data.dateOfBirth}</div>
            <div>Phone: {context.data.phone}</div>
            {allergies && (
              <div className="text-red-600 font-medium">Allergies: {allergies}</div>
            )}
          </div>
        );
      }
      case 'appointment': {
        const appointmentReason = typeof context.data.reason === 'string'
          ? context.data.reason
          : context.data.reason?.en || context.data.reason?.ar || '';
        return (
          <div className="text-xs text-gray-600 mt-1">
            <div>{context.data.date} at {context.data.time}</div>
            <div>Reason: {appointmentReason}</div>
            <div className={`font-medium ${
              context.data.status === 'completed' ? 'text-green-600' :
              context.data.status === 'cancelled' ? 'text-red-600' : 'text-blue-600'
            }`}>
              Status: {context.data.status}
            </div>
          </div>
        );
      }
      case 'record': {
        const diagnosis = typeof context.data.diagnosis === 'string'
          ? context.data.diagnosis
          : context.data.diagnosis?.en || context.data.diagnosis?.ar || '';
        return (
          <div className="text-xs text-gray-600 mt-1">
            <div>Visit: {context.data.visitType || context.data.type}</div>
            <div className="font-medium">Diagnosis: {diagnosis.substring(0, 50)}{diagnosis.length > 50 ? '...' : ''}</div>
          </div>
        );
      }
      case 'fullPage':
      case 'page':
        return (
          <div className="text-xs text-gray-600 mt-1">
            <div>View: {context.data.currentView}</div>
            <div>Patients: {context.data.totalPatients}</div>
            <div>Today's Appointments: {context.data.todaysAppointments}</div>
            {expandedContexts.has(context.id) && context.data.pageSpecificData && (
              <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                <div className="font-medium text-gray-700">Page Details:</div>
                <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-32">
                  {JSON.stringify(context.data.pageSpecificData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (contexts.length === 0) {
    return (
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-4 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700">AI Context Manager</h4>
          <Button
            onClick={onAddFullPage}
            size="xs"
          >
            <Plus />
            Add Current Page
          </Button>
        </div>
        <div className="text-center py-4">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm">
            <FileText className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">No context selected</p>
          <p className="text-xs text-gray-400 mt-1">
            Add pages, patients, appointments, or records for AI analysis
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border border-blue-200 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
          <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
            <Layers className="w-3.5 h-3.5 text-white" />
          </div>
          Active Context ({contexts.length})
        </h4>
        <div className="flex gap-2">
          <Button
            onClick={onAddFullPage}
            size="xs"
          >
            <Eye />
            Current Page
          </Button>
          <Button
            onClick={onClearAll}
            variant="outline"
            size="xs"
          >
            <Trash2 />
            Clear All
          </Button>
        </div>
      </div>
      
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {contexts.map((context) => (
          <div
            key={context.id}
            className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className={`w-8 h-8 bg-gradient-to-r ${getContextColor(context.type)} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  {getContextIcon(context.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900 text-sm">{context.label}</div>
                    {context.type === 'page' && (
                      <button
                        onClick={() => toggleExpanded(context.id)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {expandedContexts.has(context.id) ? 
                          <ChevronUp className="w-3.5 h-3.5" /> : 
                          <ChevronDown className="w-3.5 h-3.5" />
                        }
                      </button>
                    )}
                  </div>
                  {getContextDetails(context)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => onRemove(context.id)}
                className="ms-2 hover:text-[var(--color-error)]"
                aria-label="Remove context"
              >
                <X />
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-3 pt-3 border-t border-blue-100">
        <p className="text-xs text-blue-700 font-medium flex items-center gap-1.5">
          <Brain className="w-3.5 h-3.5" />
          MAIE AI has access to all {contexts.length} context{contexts.length > 1 ? 's' : ''} for intelligent analysis
        </p>
      </div>
    </div>
  );
};